import { useUserManagementStore } from "src/hooks/stores";
import { SuccessModalContent, FailureModalContent, Modal } from "@ucchub/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { UserTypes } from "@hub/common-libs";
import type {
  FailedUserManagementData,
  FilterValues,
} from "../UserManagement.types";
import { User } from "@interfaces/user";
import DeactivateUserWarningModalContent from "./DeactivateUserWarningModalContent/DeactivateUserWarningModalContent";
import DeactivateSelectUserByOrgInput from "./DeactivateSelectUserByOrgInput/DeactivateSelectUserByOrgInput";

type DeactivateUserModalProps = {
  show: boolean;
  userId: string;
  user: User;
  onClose: () => void;
  reloadList: () => void;
};

const DeactivateUserModal = ({
  userId,
  user,
  show,
  onClose,
  reloadList,
}: DeactivateUserModalProps): JSX.Element => {
  const { t } = useTranslation();
  const [delegateUserId, setDelegateUserId] = useState<string>("");

  const deactivateUsers = useUserManagementStore(
    (state) => state.deactivateUsers.call
  );
  const deactivateLoading = useUserManagementStore(
    (state) => state.deactivateUsers.loading
  );
  const [deactivationAttempted, setDeactivateAttempted] = useState(false);
  const [deactivatedUsers, setDeactivatedUsers] = useState<
    Array<UserTypes.UserModel>
  >([]);
  const [failedDeactivation, setFailedDeactivation] = useState<
    Array<FailedUserManagementData>
  >([]);
  const [message, setMessage] = useState({
    headerMessage: "",
    subHeaderMessage: "",
  });

  const close = () => {
    setDeactivateAttempted(false);
    setFailedDeactivation([]);
    setDeactivatedUsers([]);
    onClose();
  };

  const handleDeactivateConfirmed = () => {
    const deactivatePromise =
      delegateUserId.length > 0
        ? deactivateUsers(user, delegateUserId)
        : deactivateUsers(user);

    deactivatePromise
      .then((res) => {
        setMessage({
          headerMessage: t("shared:users:modal:deactivate_success.message"),
          subHeaderMessage: t("shared:users:modal:deactivate_success.message1"),
        });
        setFailedDeactivation([]);
        setDeactivatedUsers(deactivatedUsers);
        if (res.data.failed) setFailedDeactivation(res.data.failed);
      })
      .catch((error) => {
        setMessage({
          headerMessage: error?.message || "error failed",
          subHeaderMessage: error?.data?.message || "error message",
        });
        setDeactivatedUsers([]);
        setFailedDeactivation(failedDeactivation);
      })
      .finally(() => {
        setDeactivateAttempted(true);
        reloadList();
      });
  };

  const filterData: FilterValues = {
    orgId: user.organisation?.id,
    role: undefined,
    status: user && user.status,
  };

  const getModalContent = () => {
    if (deactivationAttempted) {
      if (failedDeactivation.length > 0) {
        return (
          <FailureModalContent
            heading={t("shared:users:deactivate_user_txt")}
            message={message.headerMessage}
            body={message.subHeaderMessage}
            failedUser={failedDeactivation}
            closeMessage={"Close"}
            onClose={close}
            loading={deactivateLoading}
          />
        );
      } else {
        return (
          <SuccessModalContent
            heading={message.headerMessage}
            body={message.subHeaderMessage}
            confirmText={t("shared:users:modal:deactivate_success.back")}
            onConfirm={close}
          />
        );
      }
    } else {
      return (
        <>
          <DeactivateUserWarningModalContent
            heading={t("shared:users:modal:deactivate_user_txt")}
            message={t("shared:users:modal:deactivate_message")}
            body={
              <DeactivateSelectUserByOrgInput
                orgFilter={filterData}
                userId={userId}
                setDelegateUserId={setDelegateUserId}
              />
            }
            cancelMessage={t("core:common:btn_cancel") || "Cancel"}
            onCancel={close}
            confirmText={t("shared:users:modal:deactivate_confirm_text")}
            onConfirm={handleDeactivateConfirmed}
            loading={deactivateLoading}
            onClose={close}
          />
        </>
      );
    }
  };

  return (
    <Modal
      srName="deactivate-user-modal"
      isOpen={show}
      isClosable={!deactivateLoading}
      size={2}
      animate
      onClose={close}
    >
      {getModalContent()}
    </Modal>
  );
};

export default DeactivateUserModal;
